import { createContext, useContext } from "react";
import type { KpiDef } from "@playbookai/types";

/**
 * Host-provided KPI catalogue for the dashboard KPI editors' pick-lists
 * (`singleKpi` / `chart` / `kpiWatchlist` / `kpiTable`). The host supplies the
 * SAME catalogue it uses to resolve widget bodies inside `EntityDashboard`'s
 * `renderWidget`, so the editor's choices and the rendered body can never drift.
 *
 * Keeps `@playbookai/ui` data-blind — the package depends on the shape, not on
 * any fetch/store. Co-located with the KPI editors (NOT in `shell/`) so the
 * generic `EntityDashboard` never imports anything KPI-aware. Mirrors the
 * `entity-resolver` precedent: `useKpiCatalog()` returns `null` when no provider
 * is mounted, so a KPI editor renders a clear empty-state instead of crashing.
 */
export interface KpiCatalog {
  /** Loaded active KPIs plus any persisted selections resolved by id. */
  readonly kpis: readonly KpiDef[];
  readonly archivedKpiIds?: readonly string[];
  readonly isLoading?: boolean;
  readonly isLoadingMore?: boolean;
  readonly error?: string | null;
  readonly hasMore?: boolean;
  readonly searchQuery?: string;
  /** Opening an editor is the explicit boundary that enables network reads. */
  readonly activate?: (selectedIds: readonly string[]) => void;
  readonly onSearchChange?: (query: string) => void;
  readonly onLoadMore?: () => void;
  readonly onRetry?: () => void;
}

const Ctx = createContext<KpiCatalog | null>(null);

export const KpiCatalogProvider = Ctx.Provider;

export function useKpiCatalog(): KpiCatalog | null {
  return useContext(Ctx);
}
